/**
 * usePartAliasCache — module-level cache + batched fetch for part aliases.
 *
 * Why this exists:
 * Many list/modal endpoints return only `{jp_part_no, part_name, quantity}`
 * without `oe_part_no` / `legacy_jp_part_no`. Yet our PartNoLabel needs
 * those fields to render the canonical "JPSEV1796 / Ref: 200863-(MB)"
 * subline that operators rely on to recognise the part.
 *
 * Rather than refactoring every backend route to enrich its payload, this
 * hook lazily fetches alias data from POST /api/parts/bulk-alias-lookup
 * and stashes results in a module-level Map. Subsequent calls for the
 * same JP are free (no network).
 *
 * Usage:
 *   const aliasMap = usePartAliasCache(['JPSEV0109', 'JPSEV1828', …]);
 *   const aliases = aliasMap['JPSEV0109'];
 *   // → { oe_part_no, legacy_jp_part_no, part_name, … } or undefined
 *
 * Robust to:
 *   - duplicates / null / undefined / empty strings in the jp list
 *   - rapid re-renders (debounced by 80ms)
 *   - missing token (falls back to no-op, returns {})
 */
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level cache: jp → alias object. Survives component unmounts;
// cleared only on full reload. ~1KB per row, OK for tens of thousands.
const cache = new Map();
const inflight = new Map(); // jp → Promise<alias>
const negativeCache = new Set(); // jps that resolved to nothing — don't refetch

function normalize(jps) {
  if (!Array.isArray(jps)) return [];
  const out = [];
  const seen = new Set();
  for (const j of jps) {
    if (!j) continue;
    const s = String(j).trim();
    if (!s || seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

async function fetchBatch(missing) {
  if (!missing.length) return;
  // Group all missing into a single in-flight request via a shared promise.
  const promise = axios.post(`${API}/parts/bulk-alias-lookup`, { codes: missing })
    .then((r) => {
      const results = (r.data && r.data.results) || {};
      for (const code of missing) {
        if (results[code]) {
          cache.set(code, results[code]);
        } else {
          negativeCache.add(code);
        }
      }
    })
    .catch(() => {
      // On error don't poison the cache — let next call retry. But avoid
      // hammering the endpoint in a tight loop by negatively caching for
      // a short while.
      for (const code of missing) negativeCache.add(code);
      setTimeout(() => { for (const code of missing) negativeCache.delete(code); }, 30000);
    })
    .finally(() => {
      for (const code of missing) inflight.delete(code);
    });
  for (const code of missing) inflight.set(code, promise);
  await promise;
}

export function usePartAliasCache(jps) {
  const [tick, setTick] = useState(0);
  const debRef = useRef(null);

  useEffect(() => {
    const list = normalize(jps);
    const missing = list.filter(
      (j) => !cache.has(j) && !inflight.has(j) && !negativeCache.has(j)
    );
    if (!missing.length) return;

    clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      await fetchBatch(missing);
      setTick((t) => t + 1);
    }, 80);

    return () => clearTimeout(debRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Array.isArray(jps) ? jps.join('|') : '']);

  // Build the response map for the caller. Always returns an object so
  // destructuring is safe even before fetch completes.
  const out = {};
  for (const j of normalize(jps)) {
    if (cache.has(j)) out[j] = cache.get(j);
  }
  // `tick` is included indirectly via the closure — re-renders will
  // re-execute this loop and pick up freshly-cached entries.
  void tick;
  return out;
}

// Imperative helper: useful for non-component code (e.g. when an event
// handler needs the alias right now). Returns a Promise<aliasMap>.
export async function ensurePartAliases(jps) {
  const list = normalize(jps);
  const missing = list.filter(
    (j) => !cache.has(j) && !inflight.has(j) && !negativeCache.has(j)
  );
  if (missing.length) await fetchBatch(missing);
  // Wait on any in-flight requests for the rest.
  const promises = list
    .filter((j) => inflight.has(j))
    .map((j) => inflight.get(j));
  if (promises.length) await Promise.all(promises);
  const out = {};
  for (const j of list) if (cache.has(j)) out[j] = cache.get(j);
  return out;
}

// Synchronous read — returns whatever's in cache right now, no fetch.
export function readPartAliasCache(jp) {
  if (!jp) return null;
  return cache.get(String(jp).trim()) || null;
}
